import React from "react";
import { List } from "react-window";
import { MoreVertical } from "lucide-react";
import {
  calculateProfileCompletion,
  getNameByIdFromList,
  formatDate,
  toTitleCase,
} from "../../lib/employeeHelpers";

const VirtualEmployeeTable = ({
  employees,
  rowHeight,
  columnWidths,
  darkMode,
  handleEdit,
  handlePhotoClick,
  designationsList,
  departmentsList,
  branchesList,
  setProfileCompletionData,
  setShowProfileCompletionModal,
  actionButtonRefs,
  openActionDropdown,
  setOpenActionDropdown,
  setSelectedEmployee,
  setDropdownPosition,
  calculateDropdownPosition, // Function to calculate dropdown position
  API_URL,
  height, // Height of the list container
}) => {
  const Row = ({ index, style }) => {
    const employee = employees[index];
    // Adjust style to account for border-collapse simulation if needed,
    // but react-window uses absolute positioning.
    // We'll use the style passed by react-window.

    return (
      <div
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
        }}
        className={`${
          darkMode ? "border-[#4C4C4C]" : "border-gray-200"
        } border-b ${
          darkMode ? "hover:bg-[#3C2C2C]" : "hover:bg-gray-50"
        } hover:cursor-pointer transition-colors duration-150`}
        onDoubleClick={() => handleEdit(employee)}
      >
        {/* Actions */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.actions }}
        >
          <div className="relative action-dropdown">
            <button
              ref={(el) => {
                if (el && actionButtonRefs.current)
                  actionButtonRefs.current[employee.id] = el;
              }}
              onClick={(e) => {
                e.stopPropagation();
                const button = e.currentTarget;
                const rect = button.getBoundingClientRect();
                if (openActionDropdown === employee.id) {
                  setOpenActionDropdown(null);
                } else {
                  setOpenActionDropdown(employee.id);
                  setSelectedEmployee(employee);
                  // Use calculateDropdownPosition if provided, otherwise fallback to default
                  if (calculateDropdownPosition) {
                    setDropdownPosition(calculateDropdownPosition(rect));
                  } else {
                    setDropdownPosition({
                      top: rect.bottom + window.scrollY + 4,
                      left: rect.left + window.scrollX,
                    });
                  }
                }
              }}
              className={`w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center ${
                darkMode
                  ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
              title="Employee Actions"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Photo */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.photo }}
        >
          <div
            className="w-8 h-8 rounded-full overflow-hidden mx-auto cursor-pointer hover:scale-110 transition-transform duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handlePhotoClick(employee);
            }}
            title="Click to view photo"
          >
            {employee.user?.photo ? (
              <img
                src={employee.user.photo}
                alt={`${employee.user?.name || "Employee Avatar"}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div
                className={`w-full h-full ${
                  darkMode
                    ? "bg-[#BB86FC] text-[#1C1C1C]"
                    : "bg-gray-300 text-gray-600"
                } flex items-center justify-center text-sm font-semibold`}
              >
                {employee.user?.name?.charAt(0)?.toUpperCase() || "E"}
              </div>
            )}
          </div>
        </div>

        {/* Sl No */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.slNo }}
        >
          {index + 1}
        </div>

        {/* Employee Id */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.empCode }}
        >
          {employee.empCode}
        </div>

        {/* Name */}
        <div
          className="px-2 py-2 text-left font-bold flex items-center"
          style={{ width: columnWidths.name }}
        >
          {toTitleCase(
            `${employee.user?.name || ""} ${employee.user?.lastName || ""}`.trim()
          )}
        </div>

        {/* Designation */}
        <div
          className="px-2 py-2 text-left font-semibold flex items-center"
          style={{ width: columnWidths.designation }}
        >
          {employee.designation?.name ||
            getNameByIdFromList(employee.designationId, designationsList)}
        </div>

        {/* Department */}
        <div
          className="px-2 py-2 text-left font-semibold flex items-center"
          style={{ width: columnWidths.department }}
        >
          {employee.department?.name ||
            getNameByIdFromList(employee.departmentId, departmentsList)}
        </div>

        {/* Branch */}
        <div
          className="px-2 py-2 text-left font-semibold flex items-center"
          style={{ width: columnWidths.branch }}
        >
          {getNameByIdFromList(employee.branchId, branchesList)}
        </div>

        {/* DOJ */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.doj }}
        >
          {formatDate(
            employee.dateOfJoin || employee.joiningDate || employee.joinDate
          )}
        </div>

        {/* Mobile */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.mobile }}
        >
          {employee.user?.phone || "N/A"}
        </div>

        {/* Email */}
        <div
          className="px-2 py-2 text-left font-semibold flex items-center whitespace-nowrap overflow-hidden text-ellipsis"
          style={{ width: columnWidths.email }}
        >
          <div
            className="truncate w-full"
            title={
              typeof employee.email === "object" && employee.email !== null
                ? employee.email.name
                : employee.email || "N/A"
            }
          >
            {typeof employee.email === "object" && employee.email !== null
              ? employee.email.name
              : employee.email || "N/A"}
          </div>
        </div>

        {/* Employment Type */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.employmentType }}
        >
          {employee.employmentType}
        </div>

        {/* Status */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.status }}
        >
          <span
            className={`px-2 py-1 rounded-none text-sm font-semibold ${
              employee.employmentStatus === "Probation"
                ? "bg-yellow-100 text-yellow-800"
                : employee.employmentStatus === "Confirmed"
                ? "bg-green-100 text-green-800"
                : employee.employmentStatus === "Resigned"
                ? "bg-red-100 text-red-800"
                : employee.employmentStatus === "Relieved"
                ? "bg-orange-100 text-orange-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {employee.employmentStatus}
          </span>
        </div>

        {/* Profile % */}
        <div
          className="px-2 py-2 text-center font-semibold flex items-center justify-center"
          style={{ width: columnWidths.profileCompletion }}
        >
          <div className="flex items-center justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const completionData = calculateProfileCompletion(employee);
                setProfileCompletionData({
                  employee: employee,
                  percentage: completionData.percentage,
                  pendingSections: completionData.pendingSections,
                });
                setShowProfileCompletionModal(true);
              }}
              className={`${
                calculateProfileCompletion(employee).percentage === 100
                  ? "w-9 h-9"
                  : "w-10 h-10"
              } rounded-full flex items-center justify-center text-sm font-bold transition-all duration-200 hover:scale-105 cursor-pointer ${
                calculateProfileCompletion(employee).percentage >= 80
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : calculateProfileCompletion(employee).percentage >= 60
                  ? "bg-yellow-500 text-white hover:bg-yellow-600"
                  : "bg-red-500 text-white hover:bg-red-600"
              }`}
              title="Click to view profile completion details"
            >
              {calculateProfileCompletion(employee).percentage}%
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <List
      height={height}
      itemCount={employees.length}
      itemSize={rowHeight}
      width="100%"
      className="employee-virtual-list"
    >
      {Row}
    </List>
  );
};

export default VirtualEmployeeTable;
